<template>
  <app-modal
    v-bind="$attrs"
    ref="modal"
    class="modal-container"
    content-class="modal-medium"
    title="通知先設定"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer
        v-slot="{ handleSubmit }"
        :initial-values="{
          mailAddressList: form.mailAddressList,
        }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <v-row>
                  <v-col>
                    <h3>回線情報</h3>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <app-label-group
                      label="回線番号"
                      :value="
                        line.wnumberMainAct
                          ? line.wnumberMainAct
                          : line.enumberMainAct
                      "
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <h3>通知設定</h3>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <app-badge
                      item="通知閾値帯域(受信)"
                      :msg="$filter.message('info_num_max', { max: 10000 })"
                      :isRequired="false"
                    />
                    <validation-provider
                      label="通知閾値帯域(受信)"
                      name="formReceiveThreshold"
                      v-model.number="formReceiveThreshold"
                      :rules="{
                        numeric: true,
                        between: { min: 0, max: 10000 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input-number
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <app-badge
                      item="通知閾値帯域(送信)"
                      :msg="$filter.message('info_num_max', { max: 10000 })"
                      :isRequired="false"
                    />
                    <validation-provider
                      label="通知閾値帯域(送信)"
                      name="formSendThreshold"
                      v-model.number="formSendThreshold"
                      :rules="{
                        numeric: true,
                        between: { min: 0, max: 10000 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input-number
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <app-badge
                  item="通知先メールアドレス"
                  :isRequired="!isDisabledEmail"
                  :msg="$filter.message('info_mail')"
                />
                <validation-array
                  name="mailAddressList"
                  v-slot="{ fields, push, remove }"
                >
                  <template v-for="(item, index) in fields" :key="item.key">
                    <v-row class="ga-2">
                      <validation-provider
                        label="通知先メールアドレス"
                        :name="`mail_${item.key}`"
                        v-model="form.mailAddressList[index]"
                        :rules="{
                          required: !isDisabledEmail,
                          is_email: true,
                          max: { length: 127 },
                          duplicate: { list: emailList(index) },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                          :disabled="isDisabledEmail"
                        />
                      </validation-provider>

                      <v-btn
                        icon="$mdiMinus"
                        size="small"
                        variant="outlined"
                        class="rounded-sm"
                        @click="removeEmail(index, remove)"
                        :disabled="isDisabledEmail"
                      />
                    </v-row>
                  </template>
                  <v-row justify="end">
                    <v-btn
                      icon="$mdiPlus"
                      size="small"
                      variant="outlined"
                      class="rounded-sm"
                      @click="addEmail(push)"
                      :disabled="
                        isDisabledEmail || form.mailAddressList.length >= 5
                      "
                    />
                  </v-row>
                </validation-array>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">
                  設定
                </v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel">
                  キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { ApplianceType, LineElement, LineType } from "@/apis/TrafficApi";
import type {
  LineInfoEntity,
  ThresholdEntity,
  ThresholdInfoGetEntity,
  ThresholdInfoUpdateEntity,
} from "@/apis/ThresholdSettingApi";
import cloneDeep from "lodash/cloneDeep";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "NotificationModify",
  props: {
    /** サービス */
    lineType: {
      type: String as PropType<LineType>,
      required: true,
    },
    /** 回線
     * NATの場合、ここまで遷移できないので、除外する */
    line: {
      type: Object as PropType<
        LineElement & { applianceType: Exclude<ApplianceType, "NAT"> | null }
      >,
      required: true,
    },
    /** 閾値超過通知設定 */
    thresholdInfo: {
      type: Object as PropType<ThresholdInfoGetEntity>,
      required: true,
    },
  },
  data() {
    const cloneInfo = cloneDeep(this.thresholdInfo);
    const thresholdToNum = (threshold: ThresholdEntity): number => {
      switch (threshold.unit) {
        case "KBPS":
          return threshold.value / 1000;
        case "MBPS":
          return threshold.value;
        case "GBPS":
          return threshold.value * 1000;
        default:
          return 0;
      }
    };
    return {
      form: {
        ...cloneInfo,
        mailAddressList:
          cloneInfo.mailAddressList.length === 0
            ? [""]
            : cloneInfo.mailAddressList,
      } as Omit<ThresholdInfoUpdateEntity, "lineInfo">,
      formSendThreshold: thresholdToNum(cloneInfo.sendThreshold),
      formReceiveThreshold: thresholdToNum(cloneInfo.receiveThreshold),
    };
  },
  computed: {
    /** POSTリクエス用回線情報 */
    lineInfo(): LineInfoEntity {
      switch (this.lineType) {
        case "ACCESS":
          return {
            lineType: this.lineType,
            enumber: this.line.enumberMainAct,
            applianceType: this.line.applianceType,
          };
        case "INTERNET":
          return {
            lineType: this.lineType,
            enumber:
              this.line.internetType === "TYPE1"
                ? this.line.enumberMainAct
                : null,
            wnumber:
              this.line.internetType === "TYPE4"
                ? this.line.wnumberMainAct
                : null,
            internetType: this.line.internetType,
            type4Id:
              this.line.internetType === "TYPE4" ? this.line.type4Id : null,
            applianceType: this.line.applianceType,
          };
        case "VN":
        case "CLOUD":
        case "VNL2L3":
          return {
            lineType: this.lineType,
            wnumber: this.line.wnumberMainAct,
          };
        case "MULTICLOUD":
          return {
            lineType: this.lineType,
            enumber: this.line.wnumberMainAct ? null : this.line.enumberMainAct,
            wnumber: this.line.wnumberMainAct ? this.line.wnumberMainAct : null,
            cloudServiceMenu: this.line.wnumberMainAct
              ? null
              : this.line.cloudServiceMenu,
          };
        case "CPA_5GSA":
          return {
            lineType: this.lineType,
            wnumber: this.line.wnumberMainAct,
          };
        case "SASE_GATEWAY":
          return {
            lineType: this.lineType,
            wnumber: this.line.wnumberMainAct,
          };
        default:
          throw new Error(`unknown line type. ${this.lineType}`);
      }
    },
    /** true: メールアドレス非活性, false: 活性 */
    isDisabledEmail(): boolean {
      // 送信・受信の両方が0または未入力の場合は非活性
      return (
        (this.formReceiveThreshold === null ||
          this.formReceiveThreshold === 0) &&
        (this.formSendThreshold === null || this.formSendThreshold === 0)
      );
    },
  },
  methods: {
    async submit() {
      await this.$confirm(`通知する閾値と連絡先を設定します。よろしいですか？`);
      await this.$api.thresholdSetting.update({
        ...this.form,
        sendThreshold:
          this.formSendThreshold && this.formSendThreshold !== 0
            ? { value: this.formSendThreshold, unit: "MBPS" }
            : null,
        receiveThreshold:
          this.formReceiveThreshold && this.formReceiveThreshold !== 0
            ? { value: this.formReceiveThreshold, unit: "MBPS" }
            : null,
        lineInfo: this.lineInfo,
        mailAddressList: this.isDisabledEmail ? [] : this.form.mailAddressList,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    addEmail(push: (item: any) => void) {
      const addItem = "";
      this.form.mailAddressList = [...this.form.mailAddressList, addItem];
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },

    removeEmail(index: number, remove: (idx: number) => void) {
      if (this.form.mailAddressList.length > 1) {
        this.form.mailAddressList.splice(index, 1);
        // ValidationにFieldを削除することを伝える
        remove(index);
      } else {
        this.form.mailAddressList = [""];
      }
    },

    /** 重複チェック用メール一覧 */
    emailList(index: number) {
      return this.form.mailAddressList
        ? this.form.mailAddressList.filter((_, num) => num !== index)
        : [];
    },
  },
});
</script>

<style lang="scss" scoped>
.content {
  margin-left: 1em;
}
</style>
